<script setup lang="ts">
import { defineEmits } from "vue";
import { onMounted, watch } from "vue";
import "@maersk-global/mds-components-core-tag";

import CommonDateTime from "../common/CommonDateTime.vue";
import type { PaginationAttributes } from "../../interface/pagination_interface";
import type { ValidBookingIds } from "../../interface/export/export_booking_detail_interface";
import HoldsExplanation from "../holdsExplanation/HoldsExplanation_PE018U.vue";
import HoldsExplanationTable from "../holdsExplanation/HoldsExplanation_IN103L.vue";

import { useGlobalStore } from "@/store/global";
import { appButtonClick } from "@/utils/analytics";

const globalStore = useGlobalStore();

const emit = defineEmits(["pagechange"]);

const labels = {
  yes: globalStore.sitecoreData.common.yes,
  no: globalStore.sitecoreData.common.no,
  noResults: globalStore.sitecoreData.search.not_found,
  vesselDisclaimer: globalStore.sitecoreData.container_validation.schedule_disclaimer
};

// Proops for the component
const props = defineProps({
  assets: {
    type: Array as () => ValidBookingIds[],
    required: true
  },
  currentPage: {
    type: Number,
    required: true
  },
  pagination: {
    type: Object as () => PaginationAttributes,
    required: true
  },
  columnNames: {
    type: Array as () => any,
    required: true
  },
  showHoldsExplanation: {
    type: Boolean,
    required: true
  },
  showHoldsTable: {
    type: Boolean,
    required: true
  },
  totalpages: {
    type: Number,
    required: true
  },
  analyticsTerminalDetails: {
    type: Object as () => any,
    required: true
  },
  isContainerSelected: {
    type: Boolean,
    required: true
  }
});

onMounted((): void => {
  const mcTable = document.querySelector("mc-table");

  if (mcTable && mcTable !== null) {
    const hightLightRowNumberForLoad = getCustomHighLightRowNumber("isReadyForLoad");
    const hightLightRowNumberForHolds = getCustomHighLightRowNumber("holds");
    const rowStyles = `
      .mds-table tr td:nth-child(${hightLightRowNumberForHolds}) {
        background-color: #f7f4e7 !important;
      }
      .mds-table tr td:nth-child(${hightLightRowNumberForLoad}) {
        background-color: #f7f4e7 !important;
      }
    `;

    mcTable.customstyles = rowStyles;
  }
});
const getCustomHighLightRowNumber = (columnName: string) => {
  const position = props.columnNames.findIndex((col) => col.id === columnName);
  return position === -1 ? 0 : position + 1; // +1 because nth-child is 1-based index
};

watch(
  (): any => props.isContainerSelected,
  (): void => {
    const mcTable = document.querySelector("mc-table");

    if (mcTable && mcTable !== null) {
      const hightLightRowNumberForLoad = getCustomHighLightRowNumber("isReadyForLoad");
      const hightLightRowNumberForHolds = getCustomHighLightRowNumber("holds");
      const rowStyles = `
      .mds-table tr td:nth-child(${hightLightRowNumberForHolds}) {
        background-color: #f7f4e7 !important;
      }
      .mds-table tr td:nth-child(${hightLightRowNumberForLoad}) {
        background-color: #f7f4e7 !important;
      }
    `;

      mcTable.customstyles = rowStyles;
    }
  },
  { immediate: true, deep: true }
);
const isBlankObject = (value: any) => {
  return typeof value === "object" && value !== null && Object.keys(value).length === 0 ? "" : value;
};
const paginationChangeHandler = (event) => {
  emit("pagechange", event);
};
const handleSortChange = (event: any) => {
  appButtonClick("Export Enquiry", "Sort Button Click", props.analyticsTerminalDetails.oneStreamCode, {
    ...props.analyticsTerminalDetails,
    sortColumnName: event.detail.column,
    sortDirection: event.detail.direction
  });
};
</script>

<template>
  <div class="my-table-container">
    <mc-table
      :data.prop="props.assets"
      :columns.prop="columnNames"
      :recordsperpage="pagination.pageSize"
      :currentpage="props.currentPage"
      fit="small"
      sortdefaultcolumnid="cargoCutoffDateTimeLocal"
      sortdefaultdirection="descending"
      zebraStripes
      @sortchange="handleSortChange($event)"
    >
      <div v-if="!assets.length" slot="state">{{ labels.noResults }}</div>
      <template v-for="row in assets" :key="row.id">
        <!-- Container Number -->
        <div :slot="`${row.id}_containerNumber`">
          {{ row.containerNumber }}
        </div>

        <div :slot="`${row.id}_bookingNumber`">
          {{ row.bookingNumber }}
        </div>

        <!-- Yadr Position -->
        <div :slot="`${row.id}_isReadyForLoad`" style="display: flex; justify-content: center">
          <mc-tag fit="small" :appearance="row.isReadyForLoad ? 'success' : 'error'"
            >{{ row.isReadyForLoad ? labels.yes : labels.no }}
          </mc-tag>
        </div>

        <!-- shippingLineOperator -->
        <div :slot="`${row.id}_shippingLineOperator`">
          {{ row.shippingLineOperator.toUpperCase() }}
        </div>

        <!-- Vessel Name -->
        <div :slot="`${row.id}_vesselName`">
          {{ row.vesselName }}
        </div>

        <!-- Voyage Number -->
        <div :slot="`${row.id}_outboundVoyageNumber`">
          {{ row.outboundVoyageNumber }}
        </div>

        <!--Bbegin Receive DateTime UTC time -->
        <CommonDateTime
          :id="`${row.id}_beginReceiveDateTimeLocal`"
          :actual="row.beginReceiveDateTimeLocal"
          :estimated="row.beginReceiveDateTimeLocal"
        />

        <!--Bbegin Receive DateTime UTC time -->
        <CommonDateTime
          :id="`${row.id}_cargoCutoffDateTimeLocal`"
          :actual="row.cargoCutoffDateTimeLocal"
          :estimated="row.cargoCutoffDateTimeLocal"
        />

        <!-- Is Empty -->
        <div :slot="`${row.id}_isEmpty`">
          {{ row.isEmpty ? labels.yes : labels.no }}
        </div>

        <!-- Vessel ETD -->

        <CommonDateTime
          :id="`${row.id}_appointmentDateTimeLocal`"
          :actual="row.appointmentDateTimeLocal"
          :estimated="row.appointmentDateTimeLocal"
          :showNo="true"
        />
        <CommonDateTime
          :id="`${row.id}_vesselEtd`"
          :actual="row.estimatedDepartureDateTimeLocal"
          :estimated="row.estimatedDepartureDateTimeLocal"
        />

        <!--Cargo cutoff DateTime UTC time -->
        <CommonDateTime
          :id="`${row.id}_gateInDateTimeLocal`"
          :actual="row.gateInDateTimeLocal"
          :estimated="row.gateInDateTimeLocal"
        />

        <!--Estimated or Actual DateTime UTC time -->
        <CommonDateTime
          :id="`${row.id}_estimatedDepartureDateTimeLocal`"
          :actual="row.estimatedDepartureDateTimeLocal"
          :estimated="row.estimatedDepartureDateTimeLocal"
        />

        <!--Estimated or Actual DateTime UTC time -->
        <CommonDateTime
          :id="`${row.id}_loadedOnVesselDateTimeLocal`"
          :actual="row.loadedOnVesselDateTimeLocal"
          :estimated="row.loadedOnVesselDateTimeLocal"
        />

        <!-- Has Appointment -->
        <div :slot="`${row.id}_hasAppointment`">
          {{ row.hasAppointment ? labels.yes : labels.no }}
        </div>

        <!-- Has VGM -->
        <div :slot="`${row.id}_hasVGM`">
          {{ row.hasVGM ? labels.yes : labels.no }}
        </div>

        <!-- Holds -->
        <div :slot="`${row.id}_holds`">
          <div v-if="row.holdsDescription === ''">{{ isBlankObject(row.holds) }}</div>
          <div v-else>
            <mc-tooltip fit="small" :opendelay="50" appearance="neutral-default" :zindex="99999" position="top-center">
              <span slot="trigger">{{ isBlankObject(row.holds) }}</span>
              <span class="table-small-font">{{ row.holdsDescription }}</span>
            </mc-tooltip>
          </div>
        </div>

        <div :slot="`${row.id}_reservedEquipmentOnly`">
          {{ row.reservedEquipmentOnly ? labels.yes : labels.no }}
        </div>

        <!-- Container ISO and Equipment Height -->
        <div :id="`${row.id}_equipmentTypeSizeHeight`" class="vessel-inbounds">
          <span class="color-text">{{ row.equipmentTypeSizeHeight }}</span>
        </div>

        <div :slot="`${row.id}_hazardousClass`">
          {{ row.hazardousClass }}
        </div>
      </template>
    </mc-table>
  </div>
  <div v-if="totalpages > 1" class="my-table-pagination">
    <mc-pagination
      fit="small"
      :totalpages="props.totalpages"
      previouslabel=""
      nextlabel=""
      @pagechange="paginationChangeHandler"
    />
  </div>

  <div class="spacer"></div>
  <div v-if="showHoldsExplanation">
    <HoldsExplanation journey="export" terminalOneStreamCode="PE018U" />
  </div>
  <div v-if="showHoldsTable">
    <HoldsExplanationTable journey="export" terminalOneStreamCode="IN103L" />
  </div>
  <div class="spacer"></div>
  <div class="discliamer_section">{{ labels.vesselDisclaimer }}</div>
</template>
